import type {
  MarketRateScope,
  MarketShippingSelection,
} from "@intelligems/ig-types";

/** Sorted, de-duplicated ids; `null` collections stay `null` (all products). */
export function normalizeMarketRateScope(
  scope: MarketRateScope
): MarketRateScope {
  return {
    marketId: scope.marketId,
    collectionIds:
      scope.collectionIds === null
        ? null
        : [...new Set(scope.collectionIds)].sort(),
    originLocationIds: [...new Set(scope.originLocationIds)].sort(),
  };
}

/** Stable identity of a scope, safe to compare and to use as a map key. */
export function marketRateScopeKey(scope: MarketRateScope): string {
  const value = normalizeMarketRateScope(scope);
  return JSON.stringify([
    value.marketId,
    value.collectionIds,
    value.originLocationIds,
  ]);
}

/** Stable identity of a set of scopes; the empty set is the shared set. */
export function marketRateScopeSetKey(
  scopes: MarketRateScope[] | null | undefined
): string {
  return JSON.stringify(
    [...new Set((scopes ?? []).map(marketRateScopeKey))].sort()
  );
}

export function sameMarketRateScopeSet(
  left: MarketRateScope[] | null | undefined,
  right: MarketRateScope[] | null | undefined
): boolean {
  return marketRateScopeSetKey(left) === marketRateScopeSetKey(right);
}

export function isSharedRateScope(
  scopes: MarketRateScope[] | null | undefined
): boolean {
  return !scopes || scopes.length === 0;
}

/** FNV-1a 32-bit, hex. Short, dependency-free and identical in every runtime. */
function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

const SCOPED_CODE_SEPARATOR = "__ig_scope_";

/**
 * The carrier `service_code` a rate serves under. A rate group that serves at
 * a chosen set of coverages gets a code no other set can produce, so orders
 * and reports can tell which coverage's rate was bought. Shared rates keep
 * their code unchanged, which is what every pre-scoping test and report
 * relies on. Placements do not admit by code: Shopify matches a carrier rate
 * group's services by `service_name` (see `admittedServiceNames`).
 */
export function scopedServiceCode(
  code: string,
  scopes: MarketRateScope[] | null | undefined
): string {
  if (isSharedRateScope(scopes)) {
    return code;
  }
  return `${code}${SCOPED_CODE_SEPARATOR}${fnv1a(marketRateScopeSetKey(scopes))}`;
}

export interface MarketRateScopeSourceGroup {
  id: string;
  collectionIds: string[] | null;
  originLocationIds: string[];
}

export interface MarketRateScopeSourceOption {
  marketId: string;
  optionId: string;
  rateGroups: MarketRateScopeSourceGroup[];
}

export interface DerivedMarketRateScope {
  key: string;
  scope: MarketRateScope;
  /** Merchant rate groups (by option) whose coverage this scope stands in for. */
  sources: { optionId: string; rateGroupId: string }[];
}

/**
 * One scope per merchant rate group the selections remove, de-duplicated by
 * coverage. This is the exact rule `planPlacements` uses to place the
 * Intelligems carrier, so a rate group authored for a derived scope always has
 * a placement to serve it. Selections whose option is not in `options` are
 * skipped: the caller decides whether that is an error.
 */
export function marketRateScopesForSelections(
  selections: MarketShippingSelection[],
  options: MarketRateScopeSourceOption[]
): DerivedMarketRateScope[] {
  const derived = new Map<string, DerivedMarketRateScope>();
  for (const selection of selections) {
    const option = options.find(
      (candidate) =>
        candidate.marketId === selection.marketId &&
        candidate.optionId === selection.optionId
    );
    if (!option) {
      continue;
    }
    const groups =
      selection.kind === "rate" || selection.kind === "rate_group"
        ? option.rateGroups.filter(
            (group) => group.id === selection.rateGroupId
          )
        : option.rateGroups;
    for (const group of groups) {
      const scope = normalizeMarketRateScope({
        marketId: selection.marketId,
        collectionIds: group.collectionIds,
        originLocationIds: group.originLocationIds,
      });
      const key = marketRateScopeKey(scope);
      const entry = derived.get(key) ?? { key, scope, sources: [] };
      if (
        !entry.sources.some(
          (source) =>
            source.optionId === option.optionId &&
            source.rateGroupId === group.id
        )
      ) {
        entry.sources.push({
          optionId: option.optionId,
          rateGroupId: group.id,
        });
      }
      derived.set(key, entry);
    }
  }
  return [...derived.values()];
}
